import { startOfMonth, startOfWeek, subMonths } from "date-fns";
import {
  Building2,
  Calendar,
  Package,
  Ship,
  Tag,
  Truck,
  Users,
} from "lucide-react";
import { FilterLiveForm, useGetIdentity, useTranslate } from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { DateInput } from "@/components/admin/date-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { FilterCategory } from "../filters/FilterCategory";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import {
  FREIGHT_TERMS_CHOICES,
  LOAD_STATUS_CHOICES,
  LOAD_TYPE_CHOICES,
  SHIPMENT_METHOD_CHOICES,
} from "./LoadInputs";

const BTN = "w-auto md:w-full justify-between h-10 md:h-8";

export const LoadListFilter = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  return (
    <ResponsiveFilters
      searchInput={{
        placeholder: translate("resources.loads.filters.search", {
          _: "Search loads…",
        }),
      }}
    >
      <Accordion type="multiple" className="w-full">

        {/* Status */}
        <FilterCategory
          label="resources.loads.fields.status"
          icon={<Truck className="h-4 w-4" />}
          accordionValue="status"
        >
          {LOAD_STATUS_CHOICES.map((s) => (
            <ToggleFilterButton
              key={s.id}
              className={BTN}
              label={s.name}
              value={{ status: s.id }}
            />
          ))}
        </FilterCategory>

        {/* Load Type */}
        <FilterCategory
          label="resources.loads.fields.load_type"
          icon={<Tag className="h-4 w-4" />}
          accordionValue="load_type"
        >
          {LOAD_TYPE_CHOICES.map((t) => (
            <ToggleFilterButton
              key={t.id}
              className={BTN}
              label={t.name}
              value={{ load_type: t.id }}
            />
          ))}
        </FilterCategory>

        {/* Pickup Date */}
        <FilterCategory
          label="resources.loads.fields.pickup_date"
          icon={<Calendar className="h-4 w-4" />}
          accordionValue="pickup_date"
        >
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.loads.filters.this_week", { _: "This week" })}
            value={{ "pickup_date@gte": startOfWeek(new Date()).toISOString().split("T")[0] }}
          />
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.loads.filters.this_month", { _: "This month" })}
            value={{ "pickup_date@gte": startOfMonth(new Date()).toISOString().split("T")[0] }}
          />
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.loads.filters.last_3_months", { _: "Last 3 months" })}
            value={{ "pickup_date@gte": subMonths(new Date(), 3).toISOString().split("T")[0] }}
          />
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.loads.filters.no_pickup_date", { _: "No pickup date" })}
            value={{ "pickup_date@is": "null" }}
          />
        </FilterCategory>

        {/* Specific pickup date */}
        <AccordionItem value="pickup_date_exact" className="border-none">
          <AccordionTrigger className="py-2 hover:no-underline">
            <span className="flex flex-row items-center gap-2 font-bold text-sm">
              <Calendar className="h-4 w-4" />
              {translate("resources.loads.filters.specific_date", { _: "Specific date" })}
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-2">
            <FilterLiveForm>
              <DateInput
                source="pickup_date_eq"
                label={false}
                helperText={false}
              />
            </FilterLiveForm>
          </AccordionContent>
        </AccordionItem>

        {/* Companies */}
        <AccordionItem value="companies" className="border-none">
          <AccordionTrigger className="py-2 hover:no-underline">
            <span className="flex flex-row items-center gap-2 font-bold text-sm">
              <Building2 className="h-4 w-4" />
              {translate("resources.loads.filters.companies", { _: "Companies" })}
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-2 flex flex-col gap-2">
            <div className="flex md:flex-col flex-wrap items-start pl-4">
              <ToggleFilterButton
                className={BTN}
                label={translate("resources.loads.filters.no_vendor", { _: "No vendor" })}
                value={{ "vendor_company_id@is": "null" }}
              />
              <ToggleFilterButton
                className={BTN}
                label={translate("resources.loads.filters.no_customer", { _: "No customer" })}
                value={{ "customer_company_id@is": "null" }}
              />
            </div>
            <FilterLiveForm>
              <ReferenceInput source="vendor_company_id" reference="companies">
                <AutocompleteInput
                  label="resources.loads.fields.vendor_company_id"
                  helperText={false}
                />
              </ReferenceInput>
              <ReferenceInput source="customer_company_id" reference="companies">
                <AutocompleteInput
                  label="resources.loads.fields.customer_company_id"
                  helperText={false}
                />
              </ReferenceInput>
            </FilterLiveForm>
          </AccordionContent>
        </AccordionItem>

        {/* Owners */}
        <FilterCategory
          label="resources.loads.filters.owners"
          icon={<Users className="h-4 w-4" />}
          accordionValue="owners"
        >
          {identity && (
            <>
              <ToggleFilterButton
                className={BTN}
                label={translate("resources.loads.filters.all_my_loads", { _: "All my loads" })}
                value={{
                  "@or": {
                    "sales_owner_id@eq": identity.id,
                    "purchasing_owner_id@eq": identity.id,
                    "logistics_owner_id@eq": identity.id,
                  },
                }}
              />
              <ToggleFilterButton
                className={BTN}
                label={translate("resources.loads.filters.my_sales", { _: "My sales" })}
                value={{ sales_owner_id: identity.id }}
              />
              <ToggleFilterButton
                className={BTN}
                label={translate("resources.loads.filters.my_purchasing", { _: "My purchasing" })}
                value={{ purchasing_owner_id: identity.id }}
              />
              <ToggleFilterButton
                className={BTN}
                label={translate("resources.loads.filters.my_logistics", { _: "My logistics" })}
                value={{ logistics_owner_id: identity.id }}
              />
            </>
          )}
        </FilterCategory>

        {/* Shipment Method */}
        <FilterCategory
          label="resources.loads.fields.shipment_method"
          icon={<Ship className="h-4 w-4" />}
          accordionValue="shipment_method"
        >
          {SHIPMENT_METHOD_CHOICES.map((m) => (
            <ToggleFilterButton
              key={m.id}
              className={BTN}
              label={m.name}
              value={{ shipment_method: m.id }}
            />
          ))}
        </FilterCategory>

        {/* Freight Terms */}
        <FilterCategory
          label="resources.loads.fields.freight_terms"
          icon={<Package className="h-4 w-4" />}
          accordionValue="freight_terms"
        >
          {FREIGHT_TERMS_CHOICES.map((f) => (
            <ToggleFilterButton
              key={f.id}
              className={BTN}
              label={f.name}
              value={{ freight_terms: f.id }}
            />
          ))}
        </FilterCategory>

      </Accordion>
    </ResponsiveFilters>
  );
};
